import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Key, Plus, Trash2, Copy, Check, AlertCircle, Plug, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { NeonSelect } from '@/components/NeonSelect';

export default function BusinessHub() {
  const [team, setTeam] = useState([]);
  const [keys, setKeys] = useState([]);
  const [meta, setMeta] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: '', name: '', role: 'editor' });
  const [keyName, setKeyName] = useState('');
  const [copied, setCopied] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [t, k, m] = await Promise.all([api.get('/business/team'), api.get('/business/api-keys'), api.get('/business/meta/status')]);
    setTeam(t.data); setKeys(k.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  // Handle Meta OAuth redirect
  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;
    (async () => {
      try {
        await api.post('/business/meta/callback', { code, state });
        toast.success('Meta account connected.');
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setParams({}); load(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doInvite = async () => {
    try { await api.post('/business/team', invite); setInviteOpen(false); setInvite({ email:'',name:'',role:'editor' }); toast.success('Invitation email sent'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rmMember = async (id) => { await api.delete(`/business/team/${id}`); load(); };
  const resendInvite = async (id) => {
    try { await api.post(`/business/team/${id}/resend`); toast.success('Invitation resent'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createKey = async () => {
    if (!keyName) { toast.error('Give the key a name.'); return; }
    await api.post('/business/api-keys', { name: keyName });
    setKeyName(''); toast.success('API key created'); load();
  };
  const rmKey = async (id) => { await api.delete(`/business/api-keys/${id}`); load(); };
  const copyText = (text, id) => { navigator.clipboard.writeText(text); setCopied(id); setTimeout(()=>setCopied(null), 1500); };

  const connectMeta = async () => {
    try { const { data } = await api.get('/business/meta/oauth-url'); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnectMeta = async () => { await api.post('/business/meta/disconnect'); toast.success('Disconnected'); load(); };
  const refreshMetaToken = async () => {
    try { const { data } = await api.post('/business/meta/refresh-token'); toast.success(`Token refreshed — valid until ${new Date(data.expires_at).toLocaleDateString()}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tokenBadge = () => {
    const t = meta?.token;
    if (!t || t.status === 'unknown') return null;
    const map = {
      valid: { cls: 'bg-boosta-emerald/10 text-boosta-emerald border-boosta-emerald/20', txt: `Token valid · ${t.days_left}d left` },
      expiring: { cls: 'bg-boosta-yellow/10 text-boosta-yellow border-boosta-yellow/20', txt: `Token expiring · ${t.days_left}d left` },
      expired: { cls: 'bg-red-500/10 text-red-400 border-red-500/20', txt: 'Token expired — reconnect required' },
    };
    const m = map[t.status] || map.valid;
    return <div className={`text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${m.cls}`} data-testid="meta-token-badge">{m.txt}</div>;
  };

  return (
    <div className="space-y-7" data-testid="business-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Business Hub</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Workspace operations</h1>
      </div>

      {/* Meta connection */}
      <div className="glass rounded-2xl p-6">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-boosta-cyan/10 text-boosta-cyan flex items-center justify-center"><Plug className="w-5 h-5"/></div>
            <div>
              <div className="font-display font-bold text-xl">Meta Ads</div>
              <div className="text-sm text-white/55 mt-0.5">Connect Facebook + Instagram to publish campaigns directly from Boosta.</div>
              {meta && !meta.configured && (
                <div className="mt-3 text-xs text-boosta-yellow inline-flex items-center gap-2 bg-boosta-yellow/10 px-3 py-1.5 rounded-full border border-boosta-yellow/20">
                  <AlertCircle className="w-3 h-3"/> Add META_APP_ID & META_APP_SECRET to backend .env to enable.
                </div>
              )}
              {meta?.connected && meta?.connection && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs inline-flex items-center gap-2 bg-boosta-emerald/10 text-boosta-emerald px-3 py-1.5 rounded-full border border-boosta-emerald/20">
                    <Check className="w-3 h-3"/> Connected as <span className="font-bold">{meta.connection.meta_name}</span>
                  </div>
                  {meta.connection.ad_accounts?.length > 0 && (
                    <div className="text-xs text-white/55">
                      {meta.connection.ad_accounts.length} ad account{meta.connection.ad_accounts.length>1?'s':''} available
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">{tokenBadge()}</div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {meta?.connected ? (
              <>
                {meta?.token?.status === 'expired' ? (
                  <button onClick={connectMeta} className="btn-primary rounded-full px-5 py-2.5 text-sm" data-testid="meta-reconnect">Reconnect</button>
                ) : (
                  <button onClick={refreshMetaToken} className="btn-ghost rounded-full px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="meta-refresh-token"><RefreshCw className="w-3.5 h-3.5"/> Refresh token</button>
                )}
                <button onClick={disconnectMeta} className="btn-ghost rounded-full px-5 py-2.5 text-sm" data-testid="meta-disconnect">Disconnect</button>
              </>
            ) : (
              <button onClick={connectMeta} disabled={!meta?.configured} className="btn-primary rounded-full px-5 py-2.5 text-sm disabled:opacity-40" data-testid="meta-connect">Connect Meta</button>
            )}
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="glass rounded-2xl p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow">Team</div>
            <div className="font-display font-bold text-xl mt-1">Members & roles</div>
          </div>
          <button onClick={()=>setInviteOpen(!inviteOpen)} className="btn-primary rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="team-invite-toggle">
            <Plus className="w-4 h-4"/> Invite
          </button>
        </div>
        {inviteOpen && (
          <div className="border border-white/10 rounded-xl p-4 mb-4 space-y-3 bg-black/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input value={invite.name} onChange={e=>setInvite(p=>({...p,name:e.target.value}))} placeholder="Name" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="invite-name"/>
              <input value={invite.email} onChange={e=>setInvite(p=>({...p,email:e.target.value}))} placeholder="Email" type="email" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="invite-email"/>
              <NeonSelect value={invite.role} onChange={e=>setInvite(p=>({...p,role:e.target.value}))} data-testid="invite-role"
                options={[{value:'admin',label:'Admin'},{value:'editor',label:'Editor'},{value:'viewer',label:'Viewer'}]}/>
            </div>
            <button onClick={doInvite} className="btn-primary rounded-full px-4 py-2 text-sm" data-testid="invite-send">Send invite</button>
          </div>
        )}
        {team.length === 0 ? (
          <div className="text-white/45 text-sm py-6 text-center"><Users className="w-8 h-8 mx-auto mb-2 text-white/20"/>No teammates yet.</div>
        ) : (
          <div className="space-y-2">
            {team.map(m => (
              <div key={m.member_id} className="flex justify-between items-center py-3 px-4 border border-white/5 rounded-xl">
                <div>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-white/45">{m.email} · <span className="uppercase tracking-wider text-boosta-cyan">{m.role}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider font-bold border ${m.status === 'active' ? 'bg-boosta-emerald/15 text-boosta-emerald border-boosta-emerald/30' : 'bg-boosta-yellow/15 text-boosta-yellow border-boosta-yellow/30'}`} data-testid={`member-status-${m.member_id}`}>
                    {m.status === 'active' ? 'Active' : 'Invited'}
                  </span>
                  {m.status !== 'active' && (
                    <button onClick={()=>resendInvite(m.member_id)} title="Resend invitation" className="text-white/50 hover:text-boosta-cyan" data-testid={`member-resend-${m.member_id}`}><RefreshCw className="w-4 h-4"/></button>
                  )}
                  <button onClick={()=>rmMember(m.member_id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API keys */}
      <div className="glass rounded-2xl p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-emerald">API Access</div>
            <div className="font-display font-bold text-xl mt-1">Keys</div>
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={keyName} onChange={e=>setKeyName(e.target.value)} placeholder="Key name (e.g. Production)" className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="apikey-name"/>
          <button onClick={createKey} className="btn-primary rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="apikey-create"><Key className="w-4 h-4"/> Create</button>
        </div>
        {keys.length === 0 ? (
          <div className="text-white/45 text-sm py-6 text-center">No API keys yet.</div>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.key_id} className="flex justify-between items-center py-3 px-4 border border-white/5 rounded-xl">
                <div>
                  <div className="font-medium">{k.name}</div>
                  <div className="mono text-xs text-white/50 mt-0.5">{k.key}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={()=>copyText(k.key, k.key_id)} className="text-white/50 hover:text-white">{copied===k.key_id ? <Check className="w-4 h-4 text-boosta-emerald"/> : <Copy className="w-4 h-4"/>}</button>
                  <button onClick={()=>rmKey(k.key_id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
